function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import withStyles from 'isomorphic-style-loader/withStyles';
import cn from 'classnames';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import { windowWidth } from 'modules/Layout/selectors';
import intlShape from 'shapes/intlShape';
import { formatTimestamp } from 'helpers/datetime';
import { isManualReading } from 'helpers/externalDataSources';
import { isAggregatedPostMeal } from 'libs/StatsCalculations';
import * as selectors from 'modules/PatientResults/selectors';
import { createReadingsKPIData } from '../../../helpers/kpi';
import messages from '../../../messages';
import { LEGEND_WIDTH, SUMMARRY_WIDTH, ZINDEX_ORDER } from '../constants';
import UnitSymbolAsFraction from '../../UnitSymbolAsFraction';
import styles from './BloodGlucoseConcentrationChart.pcss';
const ResponsiveLine = /*#__PURE__*/ lazy(()=>import('@nivo/line').then((m)=>({
            default: m.ResponsiveLine
        })));
var _React_PureComponent;
class BloodGlucoseConcentrationChart extends (_React_PureComponent = React.PureComponent) {
    static getDerivedStateFromProps(props) {
        const { /* eslint-disable react/prop-types */ conversion, standards, readings, start, end, ticks, isCustomRanges, isInProgress, timestampKey, valueKey, averageData } = props;
        const all = [];
        const high = [];
        const target = [];
        const low = [];
        const timestamps = ticks || [];
        const averageAll = [];
        const averageHigh = [];
        const averageTarget = [];
        const averageLow = [];
        const highReadings = [];
        const targetReadings = [];
        const lowReadings = [];
        const lowPreMealThreshold = standards.preMeal && conversion.toDisplay(standards.preMeal.lowThreshold);
        const highPreMealThreshold = standards.preMeal && conversion.toDisplay(standards.preMeal.highThreshold);
        const lowPostMealThreshold = standards.postMeal && conversion.toDisplay(standards.postMeal.lowThreshold);
        const highPostMealThreshold = standards.postMeal && conversion.toDisplay(standards.postMeal.highThreshold);
        let firstTick;
        let lastTick;
        const tickParts = [];
        if (!ticks) {
            const daySeconds = 86400;
            firstTick = +moment.unix(start).utc().locale('en--account').startOf('day').format('X');
            lastTick = +moment.unix(end).utc().locale('en--account').startOf('day').add(1, 'days').format('X');
            const difference = (lastTick - firstTick) / daySeconds;
            const maxNofElements = props.windowWidth > 1680 ? 14 : 10;
            const multiplier = Math.max(Math.ceil(difference / maxNofElements), 1);
            for(let i = firstTick; i <= lastTick; i += daySeconds * multiplier){
                if (timestamps.length >= maxNofElements) break;
                timestamps.push(i);
            }
        } else {
            firstTick = timestamps[0];
            lastTick = timestamps[timestamps.length - 1];
            const totalDiff = lastTick - firstTick;
            // eslint-disable-next-line react/prop-types
            for(let index = 1; index < ticks.length; index++){
                const elementStart = ticks[index - 1];
                const elementEnd = ticks[index];
                const elementDiff = elementEnd - elementStart;
                tickParts.push({
                    start: elementStart,
                    end: elementEnd,
                    width: elementDiff / totalDiff
                });
            }
        }
        const stdDevs = {};
        const originalTimestamps = {};
        const flags = {};
        const manualReadings = [];
        let maxReadingValue = 0;
        let oldestReadingTimestamp = 0;
        let newestReadingTimestamp = 0;
        forEach(readings, (reading)=>{
            const x = reading[timestampKey];
            if (!oldestReadingTimestamp || oldestReadingTimestamp > x) {
                oldestReadingTimestamp = x;
            }
            if (!newestReadingTimestamp || newestReadingTimestamp < x) {
                newestReadingTimestamp = x;
            }
            const y = conversion.toDisplay(reading[valueKey]);
            if (y > maxReadingValue) {
                maxReadingValue = y;
            }
            const highConverted = isAggregatedPostMeal(reading.flags) ? highPostMealThreshold : highPreMealThreshold;
            const lowConverted = isAggregatedPostMeal(reading.flags) ? lowPostMealThreshold : lowPreMealThreshold;
            all.push({
                x,
                y
            });
            if (y > highConverted) {
                high.push({
                    x,
                    y
                });
                highReadings.push(x);
            } else if (y < lowConverted) {
                low.push({
                    x,
                    y
                });
                lowReadings.push(x);
            } else {
                target.push({
                    x,
                    y
                });
                targetReadings.push(x);
            }
            if (reading.originalTimestamp) {
                originalTimestamps[x] = reading.originalTimestamp;
            }
            flags[x] = reading.flags;
            if (isManualReading(reading)) {
                manualReadings.push(x);
            }
        });
        forEach(averageData, (averageReading)=>{
            const x = averageReading[timestampKey];
            const y = conversion.toDisplay(averageReading[valueKey]);
            if (y > maxReadingValue) {
                maxReadingValue = y;
            }
            averageAll.push({
                x,
                y
            });
            const highConverted = highPostMealThreshold && highPreMealThreshold ? Math.max(highPostMealThreshold, highPreMealThreshold) : highPostMealThreshold || highPreMealThreshold;
            const lowConverted = lowPostMealThreshold && lowPreMealThreshold ? Math.min(lowPostMealThreshold, lowPreMealThreshold) : lowPostMealThreshold || lowPreMealThreshold;
            if (y > highConverted) {
                averageHigh.push({
                    x,
                    y
                });
            } else if (y < lowConverted) {
                averageLow.push({
                    x,
                    y
                });
            } else {
                averageTarget.push({
                    x,
                    y
                });
            }
        });
        const { criticalHighReadingsNumber } = createReadingsKPIData(readings, conversion, standards);
        const defaultMaxValue = criticalHighReadingsNumber > 0 ? 600 : 500;
        const maxValue = Math.ceil(conversion.toDisplay(defaultMaxValue));
        const minValue = 0;
        let valuesScale = [
            minValue
        ];
        if (conversion.unit === 'MG_DL') {
            const step = 100;
            let valueTick = step;
            while(valueTick <= maxValue){
                valuesScale.push(valueTick);
                valueTick += step;
            }
        } else {
            valuesScale = [
                0,
                5.6,
                11.1,
                16.7,
                22.2,
                27.8
            ];
            if (criticalHighReadingsNumber) {
                valuesScale.push(33.3);
            }
        }
        return {
            data: [
                {
                    id: 'all',
                    data: all
                },
                {
                    id: 'high',
                    data: high
                },
                {
                    id: 'target',
                    data: target
                },
                {
                    id: 'low',
                    data: low
                },
                {
                    id: 'averageAll',
                    data: averageAll
                },
                {
                    id: 'averageHigh',
                    data: averageHigh
                },
                {
                    id: 'averageTarget',
                    data: averageTarget
                },
                {
                    id: 'averageLow',
                    data: averageLow
                }
            ],
            stdDevs,
            originalTimestamps,
            timestamps,
            dragTimestamps: isCustomRanges ? timestamps : [],
            flags,
            manualReadings,
            firstTick,
            lastTick,
            maxValue,
            minValue,
            valuesScale,
            isInProgress,
            tickParts,
            highReadings,
            targetReadings,
            lowReadings
        };
    }
    get inProgressTheme() {
        return {
            textColor: this.colors.placeholder,
            axis: {
                ticks: {
                    line: {
                        stroke: this.colors.placeholder,
                        strokeWidth: 1
                    },
                    text: {}
                }
            },
            grid: {
                line: {
                    stroke: this.colors.placeholder,
                    strokeWidth: 1
                }
            }
        };
    }
    get margin() {
        const { direction } = this.props;
        return {
            top: 20,
            right: direction === 'ltr' ? SUMMARRY_WIDTH : LEGEND_WIDTH,
            bottom: 40,
            left: direction === 'ltr' ? LEGEND_WIDTH : SUMMARRY_WIDTH
        };
    }
    getBorderColor(line) {
        if ([
            'averageHigh',
            'averageLow',
            'averageTarget'
        ].includes(line.serieId)) {
            return 'average';
        }
        return 'transparent';
    }
    getPointColor(line) {
        if (this.state.isInProgress) {
            return this.colors.placeholder;
        }
        if (line.id === 'high') {
            return this.colors.high;
        }
        if (line.id === 'low') {
            return this.colors.low;
        }
        if (line.id === 'target') {
            return this.colors.target;
        }
        if (line.id === 'averageHigh') {
            return this.colors.high;
        }
        if (line.id === 'averageLow') {
            return this.colors.low;
        }
        if (line.id === 'averageTarget') {
            return this.colors.target;
        }
        return this.props.isPlainValuesMode ? this.props.plainValuesColor || this.colors.plain : 'transparent';
    }
    renderCustomRangeLabel(timestamps, idx, xScale) {
        const value = timestamps[idx];
        const valueX = xScale(value);
        const fromHour = moment.unix(value).utc().format('LT');
        let style = {
            transform: 'translateX(-50%)'
        };
        // eslint-disable-next-line no-lonely-if
        if (idx === 0 || idx === timestamps.length - 1) {
            style = null;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: styles.customRange__label,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.customRange__label__textLine,
                style: {
                    left: valueX
                },
                children: /*#__PURE__*/ _jsx("span", {
                    style: style,
                    children: `${fromHour}`
                })
            })
        }, value);
    }
    renderCustomRangeLabels() {
        if (!this.props.isCustomRanges || !this.chartProps.current) {
            return null;
        }
        const { direction } = this.props;
        const { innerWidth, margin, xScale } = this.chartProps.current;
        const { dragTimestamps } = this.state;
        const labels = [];
        for(let i = 0; i < dragTimestamps.length; i++){
            labels.push(this.renderCustomRangeLabel(dragTimestamps, i, xScale));
        }
        const style = {
            width: innerWidth,
            height: margin.bottom,
            position: 'relative',
            transform: `translate(0,${-margin.bottom}px)`,
            zIndex: ZINDEX_ORDER.CustomRangeLabels
        };
        if (direction === 'ltr') {
            style.left = margin.left;
        } else {
            style.right = margin.right;
        }
        return /*#__PURE__*/ _jsx("div", {
            style: style,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.customRange__labels,
                children: labels
            })
        });
    }
    renderTargetZone({ innerWidth, yScale }) {
        if (this.state.isInProgress) {
            return null;
        }
        const { conversion, standards } = this.props;
        const lowPreMealThreshold = standards.preMeal && conversion.toDisplay(standards.preMeal.lowThreshold);
        const highPreMealThreshold = standards.preMeal && conversion.toDisplay(standards.preMeal.highThreshold);
        const lowPostMealThreshold = standards.postMeal && conversion.toDisplay(standards.postMeal.lowThreshold);
        const highPostMealThreshold = standards.postMeal && conversion.toDisplay(standards.postMeal.highThreshold);
        const criticalLow = conversion.toDisplay(standards.critical.lowThreshold);
        const criticalHigh = conversion.toDisplay(standards.critical.highThreshold);
        const width = innerWidth;
        const yTopPreMeal = standards.preMeal && yScale(highPreMealThreshold);
        const yBottomPreMeal = standards.preMeal && yScale(lowPreMealThreshold);
        const yTopPostMeal = standards.postMeal && yScale(highPostMealThreshold);
        const yBottomPostMeal = standards.postMeal && yScale(lowPostMealThreshold);
        const yTopCriticalHigh = yScale(criticalHigh);
        const yBottomCriticalLow = yScale(criticalLow);
        return /*#__PURE__*/ _jsxs("g", {
            children: [
                /*#__PURE__*/ _jsx("rect", {
                    y: yTopPreMeal,
                    width: width,
                    height: yBottomPreMeal - yTopPreMeal,
                    fill: "rgba(4, 170, 141, 0.12)"
                }),
                /*#__PURE__*/ _jsx("rect", {
                    y: yTopPostMeal,
                    width: width,
                    height: yBottomPostMeal - yTopPostMeal,
                    fill: "rgba(4, 170, 141, 0.25)"
                }),
                criticalHigh && /*#__PURE__*/ _jsx("line", {
                    x1: "0",
                    y1: yTopCriticalHigh,
                    x2: width,
                    y2: yTopCriticalHigh,
                    style: {
                        stroke: this.colors.high
                    }
                }),
                criticalLow && /*#__PURE__*/ _jsx("line", {
                    x1: "0",
                    y1: yBottomCriticalLow,
                    x2: width,
                    y2: yBottomCriticalLow,
                    style: {
                        stroke: this.colors.low
                    }
                })
            ]
        });
    }
    renderTargetZoneLabels() {
        if (this.state.isInProgress || !this.chartProps.current) {
            return null;
        }
        const { yScale, innerHeight } = this.chartProps.current;
        const { conversion, standards } = this.props;
        const criticalLow = conversion.toDisplay(standards.critical.lowThreshold);
        const criticalHigh = conversion.toDisplay(standards.critical.highThreshold);
        const yTopCriticalHigh = yScale(criticalHigh);
        const yBottomCriticalLow = yScale(criticalLow);
        const fontSize = 9;
        const { direction } = this.props;
        const style = {
            height: innerHeight,
            top: this.margin.top,
            position: 'absolute',
            zIndex: ZINDEX_ORDER.CustomRangeLabels
        };
        if (direction === 'ltr') {
            style.right = 0;
        } else {
            style.left = 50;
        }
        return /*#__PURE__*/ _jsxs("div", {
            style: style,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    y: yTopCriticalHigh,
                    className: cn(styles.targetZone__label, styles['targetZone__label--criticalHigh']),
                    style: {
                        top: yTopCriticalHigh - fontSize,
                        position: 'absolute'
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.targetZone__labelContent,
                        children: /*#__PURE__*/ _jsx("span", {
                            className: styles.labelText,
                            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.tableCriticalHigh))
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    y: yBottomCriticalLow,
                    className: cn(styles.targetZone__label, styles['targetZone__label--criticalLow']),
                    style: {
                        top: yBottomCriticalLow - fontSize,
                        position: 'absolute'
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.targetZone__labelContent,
                        children: /*#__PURE__*/ _jsx("span", {
                            className: styles.labelText,
                            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.tableCriticalLow))
                        })
                    })
                })
            ]
        });
    }
    renderAverage(seriesId) {
        const { data, innerWidth, xScale, yScale, pointSize, pointBorderWidth } = this.chartProps.current;
        const averages = data.find((series)=>series.id === seriesId);
        if (!averages || !averages.data.length) {
            return null;
        }
        const { intl, conversion } = this.props;
        const { unitSymbol } = conversion;
        const color = this.getPointColor({
            id: seriesId
        });
        const borderColor = this.getBorderColor({
            id: seriesId
        });
        return averages.data.map((avg)=>{
            const timestamp = avg.x;
            const value = avg.y;
            const tickPart = this.state.tickParts.find((part)=>timestamp >= part.start && timestamp <= part.end);
            if (!tickPart) return null;
            const xPos = xScale(tickPart.start);
            const yPos = yScale(value);
            const style = {
                position: 'absolute',
                top: yPos - pointSize / 4,
                left: xPos,
                width: innerWidth * tickPart.width,
                height: pointSize / 2,
                background: color,
                border: `${pointBorderWidth}px solid ${borderColor}`
            };
            const valueToDisplay = conversion.unit === 'MG_DL' ? String(Math.floor(value * 10) / 10) : (+value).toFixed(1);
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: style
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: styles.average__text,
                        style: {
                            position: 'absolute',
                            pointerEvents: 'none',
                            fontSize: 13,
                            top: yPos - 30,
                            left: xPos + 1
                        },
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: `${intl.formatMessage(messages.labels.average)} `
                            }),
                            /*#__PURE__*/ _jsx("b", {
                                style: {
                                    fontSize: 24
                                },
                                children: valueToDisplay
                            }),
                            " ",
                            /*#__PURE__*/ _jsx(UnitSymbolAsFraction, {
                                unitSymbol: unitSymbol
                            })
                        ]
                    })
                ]
            }, `avg-${timestamp}-${value}`);
        });
    }
    renderAverages() {
        if (!this.chartProps || !this.chartProps.current) return null;
        const { innerWidth, innerHeight, height, margin } = this.chartProps.current;
        const { direction } = this.props;
        const style = {
            width: innerWidth,
            height: innerHeight,
            top: -height,
            position: 'relative',
            zIndex: ZINDEX_ORDER.CustomRangeLabels
        };
        if (direction === 'ltr') {
            style.left = margin.left;
        } else {
            style.right = margin.right;
        }
        return /*#__PURE__*/ _jsxs("div", {
            style: style,
            children: [
                this.renderAverage('averageHigh'),
                this.renderAverage('averageTarget'),
                this.renderAverage('averageLow')
            ]
        });
    }
    renderPoint({ size, color, borderColor, borderWidth, datum }) {
        if (color === 'transparent') {
            return null;
        }
        const { highlightedReadings } = this.props;
        const { selectedPoint } = this.state;
        const { x } = datum;
        const isActive = x === this.state.activePointTimestamp;
        size = isActive ? size * 1.4 : size;
        const isSelected = selectedPoint === x;
        let point;
        if (includes(this.state.manualReadings, x)) {
            const highlight = highlightedReadings && highlightedReadings.some((item)=>item.timestamp === x);
            const halfSize = size / 2;
            point = /*#__PURE__*/ _jsxs("g", {
                transform: `translate(-${halfSize} -${halfSize}) rotate(45 ${halfSize} ${halfSize})`,
                children: [
                    isSelected && /*#__PURE__*/ _jsx("rect", {
                        y: -halfSize,
                        x: -halfSize,
                        width: size + size,
                        height: size + size,
                        fill: color,
                        style: {
                            pointerEvents: 'none',
                            opacity: 0.3
                        }
                    }),
                    /*#__PURE__*/ _jsx("rect", {
                        width: size,
                        height: size,
                        fill: color,
                        stroke: highlight ? 'black' : borderColor,
                        strokeWidth: highlight ? 1 : borderWidth,
                        style: {
                            pointerEvents: 'none'
                        }
                    })
                ]
            });
        } else if (borderColor === 'average') {
            return null;
        } else {
            const highlight = highlightedReadings && highlightedReadings.some((item)=>item.timestamp === x);
            point = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    isSelected && /*#__PURE__*/ _jsx("circle", {
                        r: size,
                        fill: color,
                        style: {
                            pointerEvents: 'none',
                            opacity: 0.3
                        }
                    }),
                    /*#__PURE__*/ _jsx("circle", {
                        r: size / 2,
                        fill: color,
                        stroke: highlight ? 'black' : borderColor,
                        strokeWidth: highlight ? 1 : borderWidth,
                        style: {
                            pointerEvents: 'none'
                        }
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsx("g", {
            children: point
        });
    }
    renderLines({ width, height, margin }) {
        const { direction } = this.props;
        const { top, right, bottom, left } = margin;
        const chartWidth = width - left - right;
        const chartHeight = height - top - bottom;
        const sideLinePosition = direction === 'ltr' ? 0 : chartWidth;
        return /*#__PURE__*/ _jsxs("g", {
            children: [
                /*#__PURE__*/ _jsx("line", {
                    x1: sideLinePosition,
                    y1: 0,
                    x2: sideLinePosition,
                    y2: chartHeight,
                    style: {
                        stroke: this.colors.placeholder,
                        strokeWidth: 1
                    }
                }),
                /*#__PURE__*/ _jsx("line", {
                    x1: 0,
                    y1: chartHeight,
                    x2: chartWidth,
                    y2: chartHeight,
                    style: {
                        stroke: this.colors.placeholder,
                        strokeWidth: 1
                    }
                })
            ]
        });
    }
    renderBorder({ innerWidth, innerHeight }) {
        return /*#__PURE__*/ _jsx("rect", {
            x: 0,
            y: 0,
            width: innerWidth,
            height: innerHeight,
            fill: "none",
            stroke: "#717070",
            strokeWidth: 1
        });
    }
    handleGridXState(chartProps) {
        this.chartProps.current = chartProps;
        if (!this.state.isReady) this.setState({
            isReady: true
        });
        return null;
    }
    renderCustomRangeLines() {
        const { timestamps } = this.state;
        if (!this.chartProps || !this.chartProps.current) return null;
        const { xScale, innerHeight, innerWidth } = this.chartProps.current;
        return /*#__PURE__*/ _jsx("div", {
            className: styles.customRange__linesWrapper,
            style: {
                height: innerHeight,
                left: 49,
                top: this.margin.top,
                width: innerWidth,
                zIndex: ZINDEX_ORDER.CustomRangeLines
            },
            children: timestamps.slice(1, -1).map((tick)=>{
                const x = xScale(tick);
                return /*#__PURE__*/ _jsx("div", {
                    className: styles.customRange__line,
                    style: {
                        left: x
                    }
                }, tick);
            })
        });
    }
    render() {
        const { data, timestamps, firstTick, lastTick, maxValue, minValue, valuesScale, isInProgress } = this.state;
        const { direction, isLineEnabled, isPlainValuesMode } = this.props;
        const allDataLength = data[0].data.length;
        const margin = {
            top: 20,
            right: 50,
            bottom: 20,
            left: 50
        };
        const axisY = {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: valuesScale
        };
        const lineColor = isInProgress ? this.colors.placeholder : this.colors.line;
        let theme = {
            crosshair: {
                line: {
                    stroke: '#30A8FF',
                    strokeWidth: 1,
                    strokeDasharray: null
                }
            },
            axis: {
                ticks: {
                    text: {
                        fill: '#4D4D4D',
                        fontSize: 10,
                        fontFamily: 'Avenir, sans-serif'
                    },
                    line: {
                        stroke: this.colors.placeholder,
                        strokeWidth: 1
                    }
                }
            }
        };
        if (isInProgress) {
            theme = this.inProgressTheme;
        }
        /**
      IMPORTANT: Because Edge doesn't support `dominant-baseline` and RTL support is poor
      hardcoded styles overwrite ticks labels transform for current tickPadding and tickRotation values and
      must be correlated if change

      Mesh crashes on hover if empty chart, so we turn it on only if allDataLength is greater than 0
      https://github.com/plouc/nivo/issues/2597

     TODO: Can we do this without AutoSizer?
     */ return /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.root),
            children: [
                /*#__PURE__*/ _jsx(Suspense, {
                    fallback: null,
                    children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                        data: data,
                        margin: margin,
                        xScale: {
                            type: 'linear',
                            min: firstTick,
                            max: lastTick,
                            reverse: direction === 'rtl'
                        },
                        yScale: {
                            type: 'linear',
                            min: minValue,
                            max: maxValue
                        },
                        axisTop: null,
                        axisRight: direction === 'ltr' ? null : axisY,
                        axisBottom: {
                            tickSize: 0,
                            tickPadding: 10,
                            tickRotation: 0,
                            tickValues: timestamps,
                            format: (value)=>this.props.formatLabel ? this.props.formatLabel(value, firstTick, lastTick, 0) : formatTimestamp(value)
                        },
                        axisLeft: direction === 'rtl' ? null : axisY,
                        enableGridX: false,
                        enableGridY: false,
                        gridXValues: timestamps,
                        gridYValues: valuesScale,
                        colors: [
                            lineColor,
                            'transparent',
                            'transparent',
                            'transparent'
                        ],
                        theme: _object_spread({}, theme, this.props.gridTheme),
                        motionConfig: "stiff",
                        enableArea: true,
                        areaOpacity: 0.1,
                        lineWidth: 1,
                        pointSize: 9,
                        pointBorderColor: this.getBorderColor,
                        pointSymbol: (props)=>this.renderPoint(props),
                        pointColor: (line)=>this.getPointColor(line),
                        pointBorderWidth: 0,
                        crosshairType: direction === 'ltr' ? 'bottom-left' : 'bottom-right',
                        useMesh: true,
                        enableCrosshair: false,
                        tooltip: ()=>null,
                        layers: [
                            'grid',
                            'markers',
                            'axes',
                            isLineEnabled ? 'areas' : null,
                            isPlainValuesMode ? null : this.renderTargetZone.bind(this),
                            isInProgress ? null : 'crosshair',
                            isLineEnabled ? 'lines' : null,
                            'slices',
                            allDataLength ? 'mesh' : null,
                            'legends',
                            this.renderLines.bind(this),
                            this.renderBorder.bind(this),
                            'points',
                            this.handleGridXState.bind(this)
                        ],
                        isInteractive: this.props.isReadOnly
                    })
                }),
                this.renderCustomRangeLines(),
                this.renderCustomRangeLabels(),
                this.renderTargetZoneLabels(),
                this.renderAverages()
            ]
        });
    }
    constructor(props){
        super(props);
        this.state = {
            data: [],
            timestamps: [],
            dragTimestamps: [],
            activePointTimestamp: null,
            // customRangesLabelsRefs: {},
            manualReadings: [],
            firstTick: null,
            lastTick: null,
            maxValue: 0,
            minValue: 0,
            valuesScale: [],
            isInProgress: false,
            tickParts: [],
            selectedPoint: null
        };
        this.colors = {
            high: '#F3BA1A',
            target: '#1EAA8E',
            low: '#E34325',
            line: '#C9CFDC',
            plain: '#30A8FF',
            placeholder: '#E0E8F2'
        };
        this.chartProps = /*#__PURE__*/ React.createRef(null);
    }
}
_define_property(BloodGlucoseConcentrationChart, "propTypes", {
    // Implicit props
    windowWidth: PropTypes.number,
    // Explicit props
    conversion: PropTypes.object.isRequired,
    direction: PropTypes.string.isRequired,
    standards: PropTypes.shape({
        maxValue: PropTypes.number.isRequired,
        minValue: PropTypes.number.isRequired,
        preMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        }),
        postMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        }),
        critical: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        })
    }),
    customRangesLabels: PropTypes.arrayOf(PropTypes.string),
    formatLabel: PropTypes.func,
    // renderTick         : PropTypes.func,
    unitSymbol: PropTypes.string,
    isLineEnabled: PropTypes.bool,
    isPlainValuesMode: PropTypes.bool,
    plainValuesColor: PropTypes.string,
    isCustomRanges: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    // printMode          : PropTypes.bool,
    enableGridY: PropTypes.bool,
    gridTheme: PropTypes.object,
    intl: intlShape,
    highlightedReadings: PropTypes.array,
    start: PropTypes.number,
    end: PropTypes.number,
    activeMeasurement: PropTypes.object,
    dailyChartHeight: PropTypes.number
});
_define_property(BloodGlucoseConcentrationChart, "defaultProps", {
    unitSymbol: null,
    enableGridY: false,
    gridTheme: {},
    dailyChartHeight: 0
});
const mapStateToProps = (state)=>({
        windowWidth: windowWidth(state),
        activeMeasurement: selectors.selectedReading(state)
    });
const ConnectedBloodGlucoseConcentrationChart = connect(mapStateToProps)(BloodGlucoseConcentrationChart);
export default withStyles(styles)(injectIntl(ConnectedBloodGlucoseConcentrationChart));
